import { useCallback, useMemo } from 'react';

type UseLoggerLevels = 'low' | 'medium' | 'high';

/**
 * Simple logging hook
 * @param {string} name
 * @param {Partial<Record<UseLoggerLevels, boolean>>} levels
 */
export const useLogger = (name: string, levels: Partial<Record<UseLoggerLevels, boolean>>) => {
	const canDebug = process.env.NODE_ENV === 'development';
	const defaultLevels = { low: false, medium: false, high: false };
	const currentLevels = { ...defaultLevels, ...levels };

	const logger = useCallback(
		(level: UseLoggerLevels, message: any, ...rest: any[]) => {
			if (!canDebug) return;
			if (!currentLevels[level]) return;
			const res = message
				.toString()
				.split(' ')
				.reduce(
					// @ts-ignore IDK why
					(acc, word) => {
						const isUuid = false;
						if (isUuid) word = `%c${word}%c`;
						return {
							message: [acc.message, word].join(' '),
							hasFormatting: acc.hasFormatting || isUuid,
						};
					},
					{ message: '', hasFormatting: false },
				);
			const levelIcon = [level === 'low' && '🔵', level === 'medium' && '🟠', level === 'high' && '🔴'].filter(Boolean);
			const args = [
				`${levelIcon} %c[${new Date().toISOString().slice(11, -1)}] %c(${name}) %c${res.message}`,
				'color: #C9C9C9; font-size:8px',
				'color: #C9C9C9; font-size:8px',
				'color: #464646',
				res.hasFormatting ? 'color: #A887FF' : undefined,
				...rest,
			].filter(Boolean);
			console.debug(...args);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[name],
	);

	const loggerLow = useCallback((message: any, ...rest: any[]) => logger('low', message, ...rest), [logger]);
	const loggerMedium = useCallback((message: any, ...rest: any[]) => logger('medium', message, ...rest), [logger]);
	const loggerHigh = useCallback((message: any, ...rest: any[]) => logger('high', message, ...rest), [logger]);

	return useMemo(() => ({ loggerLow, loggerMedium, loggerHigh }), [loggerHigh, loggerLow, loggerMedium]);
};
